import type { ReactNode } from 'react'
import { Badge } from '@/components/base/badges/badges'

/**
 * Ein Zustand, überall gleich.
 *
 * **Eine Darstellung für jeden Status** — die des Gerätezustands, weil sie
 * schon die richtige war: ein Pillenschild von Untitled UI, klein, in der
 * Farbe der Lage. Vorher stand an manchen Stellen nur farbiger Text, an
 * anderen ein selbst gebautes Schildchen mit eigenen Maßen. Wer über eine
 * Seite liest, liest dann drei Formsprachen und muss jede einzeln lernen.
 *
 * **Kein neues Bauteil**, sondern eine Zuordnung: Der Ton sagt, wie es um die
 * Sache steht; welche Farbe das ist, entscheidet diese eine Stelle. So bleibt
 * „gut" auf jeder Seite dieselbe Farbe, auch wenn sich die Palette ändert.
 *
 * Die Farbe trägt die Aussage nicht allein — das Wort steht daneben und sagt
 * dasselbe (`SECURITY.md`-nah gedacht: Rot allein ist für einen Teil der Leser
 * kein Unterschied).
 */

export type Ton =
  /** Läuft, ist da, ist gültig. */
  | 'gut'
  /** Hängt an jemandem: wartet auf Freigabe, auf Kopplung, auf einen Zugang. */
  | 'wartet'
  /** Läuft gegen eine Grenze oder ist gestört. */
  | 'stoerung'
  /** Da, aber ohne Betrieb: gesperrt, deaktiviert, widerrufen. */
  | 'ruhig'

const FARBE = {
  gut: 'success',
  wartet: 'warning',
  stoerung: 'error',
  ruhig: 'gray',
} as const

export function Status({ ton, children }: { ton: Ton; children: ReactNode }) {
  return (
    <Badge type="pill-color" size="sm" color={FARBE[ton]}>
      {children}
    </Badge>
  )
}
